import React, {ChangeEvent, Component, ReactNode} from 'react'
import {StyledPageTitle} from '../reservations/components/CommonStyle'
import {HideInPC, HideInSP} from '../../styledComponents/ResponsiveConvenience'
import PPStepper from '../shared/PPStepper'
import {PrimaryStyledButton, StyledSecondaryButton} from '../../styledComponents/Button'
import {Box} from '@mui/material'
import {votingPreferenceStepsForPc, votingPreferenceStepsForSp} from './ResidentVotingPreferenceMethodSelectionPage'
import {Nullable} from '../../app/TypeHelpers'
import {NatAsyncCallFunction, toHalfWidthNumber} from '../../__NoAutomatedTests/common/util/Natutil'
import {PROXY_TYPE, USE_FUNCTION_ID, VOTING_PREFERENCE_RESPONSE} from '../../CodeMaster'
import ResidentGeneralMeetingRepo, {ResidentProxyVoter} from './ResidentGeneralMeetingRepo'
import {ResidentVotingPreferenceProxyConfirmationLocationState} from './ResidentVotingPreferenceProxyConfirmationPage'
import NatRidgeRepo from '../../__NoAutomatedTests/components/ridge/repository/NatRidgeRepo'
import {RidgeData} from '../../__NoAutomatedTests/components/ridge/repository/NatGetRidge'
import ResidentSectionComponent from '../shared/ResidentSectionComponent'
import ResidentLabeledContentComponent from '../shared/ResidentLabeledContentComponent'
import styled from 'styled-components'
import {FullWidthColumnFlexContainer} from '../../styledComponents/FlexComponents'
import NatUseFunctionMasterRepo
    from '../../__NoAutomatedTests/components/useFunctionMaster/repository/NatUseFunctionMasterRepo'
import PPRadioGroupForResident, {ResidentRadioButtonProps} from '../shared/forms/resident/PPRadioGroupForResident'
import {PlainBackLink} from '../../styledComponents/Links'
import PPSelectForResident, {Option} from '../shared/forms/resident/PPSelectForResident'
import {PPTextFieldForResident} from '../shared/forms/resident/PPTextFieldForResident'
import {DisplayItemDividerForPageTop} from '../../styledComponents/DisplayItem'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet
} from '../shared/hoc/createResidentWithErrorHandlingPage'
import {Location} from 'react-router-dom'
import {HttpError} from '../../network/errors/HttpError'
import {FontWeights} from '../../styledComponents/Constants'

export interface ResidentVotingPreferenceProxyInputLocationState {
    votingPreferenceId: Nullable<number>
    assocId: number
    assocName: string
    votingPreferenceResponse: VOTING_PREFERENCE_RESPONSE
    residentProxyVoter: Nullable<ResidentProxyVoter>
    votingPreferenceVersion: Nullable<number>
    beforeNetRespondedAssocIdList: number[]
}

interface Props {
    location: Location<ResidentVotingPreferenceProxyInputLocationState>
    condominiumCd: string
    generalMeetingId: number
    assocId: number
    residentGeneralMeetingRepo: ResidentGeneralMeetingRepo
    useFunctionMasterRepo: NatUseFunctionMasterRepo
    ridgeRepo: NatRidgeRepo
}

interface State {
    isSmoothECondominium: boolean
    proxyTypeValue: string
    proxyVoterRidgeCd: string
    proxyVoterRoomNumber: Nullable<string>
    proxyVoterName: Nullable<string>
    ridgeList: RidgeData[]
    ridgeOptionList: Option[]
    isLoadCompleted: boolean
}

class ResidentVotingPreferenceProxyInputPage extends Component<Props & ResidentWithErrorHandlingPagePropsSet, State> {
    constructor(props: Props & ResidentWithErrorHandlingPagePropsSet) {
        super(props)

        const residentProxyVoter = this.props.location.state?.residentProxyVoter

        this.state = {
            isSmoothECondominium: false,
            proxyTypeValue: residentProxyVoter?.proxyType ? residentProxyVoter.proxyType.value : '',
            proxyVoterRidgeCd: residentProxyVoter?.proxyVoterRidgeCd ? residentProxyVoter.proxyVoterRidgeCd : '',
            proxyVoterRoomNumber:
                residentProxyVoter?.proxyVoterRoomNumber ? residentProxyVoter.proxyVoterRoomNumber : null,
            proxyVoterName: residentProxyVoter?.proxyVoterName ? residentProxyVoter.proxyVoterName : null,
            ridgeList: [],
            ridgeOptionList: [],
            isLoadCompleted: false,
        }

        if (!this.props.location.state) {
            this.props.navigate('/resident/error')
            return
        }

        this.handleProxyButtonClick = this.handleProxyButtonClick.bind(this)
        this.handleBackButtonClick = this.handleBackButtonClick.bind(this)
        this.handleProxyTypeChange = this.handleProxyTypeChange.bind(this)
        this.handleChangeProxyType = this.handleChangeProxyType.bind(this)
        this.handleChangeRidgeName = this.handleChangeRidgeName.bind(this)
        this.handleChangeRoomNumber = this.handleChangeRoomNumber.bind(this)
        this.handleChangeName = this.handleChangeName.bind(this)
        this.renderProxyVoterInputArea = this.renderProxyVoterInputArea.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const residentProxyVoter = this.props.location.state?.residentProxyVoter

        const {
            condominiumCd,
            useFunctionMasterRepo,
            ridgeRepo,
            navigate,
        } = this.props

        const {
            proxyTypeValue,
        } = this.state

        await this.props.eventEmitter.showLoading()
        try {
            const getValidUseFunctionLoggedInResponse = await NatAsyncCallFunction(
                useFunctionMasterRepo.getValidUseFunctionLoggedIn.bind(
                    useFunctionMasterRepo,
                ),
                navigate
            )

            if (!getValidUseFunctionLoggedInResponse || !getValidUseFunctionLoggedInResponse.details) {
                return
            }

            const getRidgeListResponse = await NatAsyncCallFunction(
                ridgeRepo.getRidgeList.bind(
                    ridgeRepo,
                    condominiumCd,
                ),
                navigate
            )

            if (!getRidgeListResponse || !getRidgeListResponse.details) {
                return
            }

            let proxyVoterRidgeCd = residentProxyVoter?.proxyVoterRidgeCd ?? ''

            if (getRidgeListResponse.details.length === 1) {
                proxyVoterRidgeCd = getRidgeListResponse.details[0].ridgeCd
            }

            const ridgeOptionList = getRidgeListResponse.details
                .map((ridge) => ({
                    value: ridge.ridgeCd,
                    label: ridge.ridgeName || '棟名なし',
                }))

            ridgeOptionList.unshift(
                {
                    label: '',
                    value: '',
                }
            )

            const isSmoothECondominium = getValidUseFunctionLoggedInResponse.details.validUseFunctionIdList
                .includes(Number(USE_FUNCTION_ID.SMOOTH_E.value))

            this.setState({
                proxyTypeValue: isSmoothECondominium ? PROXY_TYPE.PROXY_VOTER.value : proxyTypeValue,
                proxyVoterRidgeCd: proxyVoterRidgeCd,
                ridgeList: getRidgeListResponse.details,
                ridgeOptionList: ridgeOptionList,
                isSmoothECondominium: isSmoothECondominium,
                isLoadCompleted: true,
            })
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    handleProxyTypeChange(event: React.ChangeEvent<HTMLInputElement>): void {
        const proxyType = PROXY_TYPE.codeToEnumConstants(event.target.value)
        if (proxyType) {
            this.setState({proxyTypeValue: proxyType.value})
        }
    }

    async handleProxyButtonClick(): Promise<void> {
        const {
            condominiumCd,
            generalMeetingId,
            assocId,
            residentGeneralMeetingRepo,
            eventEmitter,
            navigate,
        } = this.props

        const {
            votingPreferenceId,
            assocName,
            votingPreferenceVersion,
            beforeNetRespondedAssocIdList,
        } = this.props.location.state

        const {
            proxyTypeValue,
            proxyVoterRidgeCd,
            proxyVoterRoomNumber,
            proxyVoterName,
            ridgeList,
        } = this.state

        let proxyVoter: Nullable<ResidentProxyVoter> = null
        if (proxyTypeValue === PROXY_TYPE.CHAIRMAN.value) {
            proxyVoter = {
                proxyType: PROXY_TYPE.CHAIRMAN,
                proxyVoterRidgeCd: null,
                proxyVoterRidgeName: null,
                proxyVoterRoomNumber: null,
                proxyVoterName: null,
            }
        } else if (proxyTypeValue === PROXY_TYPE.PROXY_VOTER.value) {
            const selectedRidge = ridgeList.find(r => r.ridgeCd === proxyVoterRidgeCd)
            if (!proxyVoterRoomNumber || !proxyVoterName) {
                return
            }
            proxyVoter = {
                proxyType: PROXY_TYPE.PROXY_VOTER,
                proxyVoterRidgeCd: selectedRidge ? selectedRidge.ridgeCd : null,
                proxyVoterRidgeName: selectedRidge ? selectedRidge.ridgeName : null,
                proxyVoterRoomNumber: toHalfWidthNumber(proxyVoterRoomNumber).padStart(4, '0'),
                proxyVoterName: proxyVoterName,
            }
        }

        await eventEmitter.showLoading()
        try {
            const response = await this.props.asyncCallFunction(
                // @ts-ignore
                residentGeneralMeetingRepo.saveGeneralMeetingVotingPreference.bind(
                    residentGeneralMeetingRepo,
                    condominiumCd,
                    generalMeetingId,
                    assocId,
                    votingPreferenceId,
                    VOTING_PREFERENCE_RESPONSE.PROXY,
                    proxyVoter,
                    null,
                    votingPreferenceVersion,
                    false,
                ),
                async (httpErrorOrNetworkError) => {
                    if (httpErrorOrNetworkError instanceof HttpError) {
                        const requestItemErrors = httpErrorOrNetworkError.responseBody.requestItemErrors

                        if (requestItemErrors.length === 0) {
                            navigate(
                                `/resident/condominiums/${condominiumCd}/general-meetings/${generalMeetingId}`,
                                {
                                    state: {
                                        votingPreferenceHttpError: {
                                            status: httpErrorOrNetworkError.status,
                                            responseBody: httpErrorOrNetworkError.responseBody,
                                        },
                                    },
                                },
                            )
                        } else {
                            await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                        }
                    }
                },
            )

            if (!response || response.requestError !== null) {
                return
            }

            const locationState: ResidentVotingPreferenceProxyConfirmationLocationState = {
                votingPreferenceId: votingPreferenceId,
                assocId: assocId,
                assocName: assocName,
                votingPreferenceResponse: VOTING_PREFERENCE_RESPONSE.PROXY,
                votingPreferenceVersion: votingPreferenceVersion,
                residentProxyVoter: proxyVoter,
                showRidgeItem: ridgeList.length > 1,
                beforeNetRespondedAssocIdList: beforeNetRespondedAssocIdList,
            }

            navigate(
                `/resident/condominiums/${condominiumCd}/general-meetings/${generalMeetingId}/assocs/${assocId}/voting-preference/confirmation/proxy`,
                {
                    state: locationState,
                },
            )
        } finally {
            eventEmitter.closeLoading()
        }
    }

    handleBackButtonClick(): void {
        const {
            condominiumCd,
            generalMeetingId,
            assocId
        } = this.props

        this.props.navigate(
            `/resident/condominiums/${condominiumCd}/general-meetings/${generalMeetingId}/assocs/${assocId}/voting-preference/method-selection`,
        )
    }

    handleChangeProxyType(event: ChangeEvent<HTMLInputElement>): void {
        this.setState({
            proxyTypeValue: event.target.value
        })
    }

    handleChangeRidgeName(event: React.ChangeEvent<HTMLSelectElement>): void {
        this.setState({proxyVoterRidgeCd: event.target.value})
    }

    handleChangeRoomNumber(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({proxyVoterRoomNumber: event.target.value})
    }

    handleChangeName(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({proxyVoterName: event.target.value})
    }

    getProxyTypeOptions(): ResidentRadioButtonProps[] {
        return PROXY_TYPE.values().map((proxyType) => {
            let label
            if (proxyType === PROXY_TYPE.CHAIRMAN) {
                label = '議長へ委任'
            } else {
                label = '代理人を指定して委任'
            }
            return {
                label: label,
                value: proxyType.value
            }
        })
    }

    renderProxyVoterInputArea(): React.ReactNode {
        const {
            proxyTypeValue,
            proxyVoterRidgeCd,
            proxyVoterRoomNumber,
            proxyVoterName,
            ridgeList,
            ridgeOptionList,
        } = this.state

        if (proxyTypeValue !== PROXY_TYPE.PROXY_VOTER.value) {
            return
        }

        return <>
            {ridgeList.length > 1 &&
                <ResidentLabeledContentComponent
                    label={'棟名'}
                    labelPosition={'center'}
                    required
                    content={
                        <PPSelectForResident
                            inputProps={{
                                value: proxyVoterRidgeCd,
                            }}
                            placeholder={'棟名を選択してください'}
                            onChange={this.handleChangeRidgeName}
                            options={ridgeOptionList}
                            eventEmitter={this.props.eventEmitter}
                        />
                    }
                />
            }

            <ResidentLabeledContentComponent
                label={'部屋番号'}
                labelPosition={'center'}
                required
                content={
                    <PPTextFieldForResident
                        inputProps={{
                            value: proxyVoterRoomNumber,
                            maxLength: 4,
                            placeholder: '例：0101',
                        }}
                        onChange={this.handleChangeRoomNumber}
                        defaultValue={proxyVoterRoomNumber}
                        eventKeys={['residentProxyVoter.proxyVoterRoomNumber']}
                        eventEmitter={this.props.eventEmitter}
                    />
                }
            />

            <ResidentLabeledContentComponent
                label={'氏名'}
                labelPosition={'center'}
                required
                content={
                    <PPTextFieldForResident
                        inputProps={{
                            value: proxyVoterName,
                            maxLength: 21,
                            placeholder: '例：素敵 太郎',
                        }}
                        onChange={this.handleChangeName}
                        defaultValue={proxyVoterName}
                        eventKeys={['residentProxyVoter.proxyVoterName']}
                        eventEmitter={this.props.eventEmitter}
                    />
                }
            />
        </>
    }

    render(): ReactNode {
        if (!this.props.location.state) {
            return null
        }

        const {assocName} = this.props.location.state

        const {
            condominiumCd,
            generalMeetingId,
            assocId
        } = this.props

        const {
            proxyTypeValue,
            proxyVoterRidgeCd,
            proxyVoterRoomNumber,
            proxyVoterName,
            isSmoothECondominium,
            isLoadCompleted,
        } = this.state

        if (!isLoadCompleted) {
            return null
        }

        let proxyButtonDisabled = true

        if (proxyTypeValue === PROXY_TYPE.CHAIRMAN.value) {
            proxyButtonDisabled = false
        } else if (proxyTypeValue === PROXY_TYPE.PROXY_VOTER.value) {
            if (proxyVoterRidgeCd && proxyVoterRoomNumber && proxyVoterName) {
                proxyButtonDisabled = false
            }
        }

        return <>
            <PlainBackLink
                to={`/resident/condominiums/${condominiumCd}/general-meetings/${generalMeetingId}/assocs/${assocId}/voting-preference/method-selection`}>
                戻る
            </PlainBackLink>
            <StyledDisplayItemDividerForPageTop/>
            <HideInSP>
                <PPStepper steps={votingPreferenceStepsForPc} activeStep={1}/>
            </HideInSP>
            <HideInPC>
                <PPStepper steps={votingPreferenceStepsForSp} activeStep={1} alternativeLabel/>
            </HideInPC>
            <Box height={'30px'}/>
            <StyledPageTitle>議決権の委任</StyledPageTitle>
            <Box height={'20px'}/>
            <div>
                管理組合の総会開催要項をご確認のうえ、委任方法を選択し、議決権を委任してください。
            </div>
            <Box height={'20px'}/>
            <ResidentSectionComponent
                title={'対象管理組合'}
                content={
                    <AssocName>{assocName}</AssocName>
                }
            />
            <Box height={'20px'}/>

            <ResidentSectionComponent
                title={'意思表明'}
                content={<VotingPreferenceInputArea>
                    {isSmoothECondominium ?
                        <ResidentLabeledContentComponent
                            label={'委任先'}
                            labelPosition={'center'}
                            required
                            content={<Box fontWeight={'bold'}>
                                代理人を指定して委任
                            </Box>
                            }
                        />
                        :
                        <ResidentLabeledContentComponent
                            label={'委任先'}
                            labelPosition={'center'}
                            required
                            content={<>
                                <PPRadioGroupForResident
                                    data={this.getProxyTypeOptions()}
                                    selectValue={proxyTypeValue}
                                    onChange={this.handleChangeProxyType}
                                    eventKeys={['proxyType']}
                                    eventEmitter={this.props.eventEmitter}/>
                            </>
                            }
                        />
                    }
                    {this.renderProxyVoterInputArea()}
                </VotingPreferenceInputArea>}
            />
            <Box height={'20px'}/>
            <PrimaryStyledButton
                onClick={this.handleProxyButtonClick}
                disabled={proxyButtonDisabled}
            >
                議決権を委任する
            </PrimaryStyledButton>
            <Box height={'30px'}/>
            <StyledSecondaryButton onClick={this.handleBackButtonClick}>
                戻る
            </StyledSecondaryButton>
            <Box height={'15px'}/>
        </>
    }
}

export default createResidentWithErrorHandlingPage(ResidentVotingPreferenceProxyInputPage)

const StyledDisplayItemDividerForPageTop = styled(DisplayItemDividerForPageTop)`
  margin-top: 14px;
  margin-bottom: 15px;
`

const AssocName = styled.div`
  font-weight: ${FontWeights.Normal};
  overflow-wrap: anywhere;
  line-break: anywhere;
`

const VotingPreferenceInputArea = styled(FullWidthColumnFlexContainer)`
  row-gap: 20px;
`

